/**
 * Session-token hash helper for WhatsApp closed-loop attribution.
 *
 * `posthog.get_session_id()` returns a UUID like
 * `0196f3c2-c43b-79b0-9a72-3c2c7a8f9e4d` — far too long to embed
 * unobtrusively in a WhatsApp prefill. We hash it down to 4 base32 chars
 * which is enough cardinality for the few-thousand-WA-clicks/month scale
 * (collisions just produce noise the backend's phone fallback resolves).
 *
 * Deterministic, pure, no PostHog dep — testable in isolation.
 */

const ALPHABET = 'abcdefghijklmnopqrstuvwxyz234567' // RFC 4648 base32 lowercase
const TOKEN_LEN = 4

/**
 * Hash a session id to a 4-char lowercase base32 token. Returns an empty
 * string when the input is empty/null so call sites can branch cleanly
 * (the WA-prefill builder skips the token line altogether when empty).
 *
 * The hash is FNV-1a 32-bit — fast, small, collision-resistant enough
 * at 4-char (32^4 = ~1M buckets) for our volume. We don't use it for
 * security, only for grouping repeat clicks from the same browser.
 */
export function hashSessionId(sessionId: string | null | undefined): string {
  if (!sessionId) return ''

  let hash = 0x811c9dc5 // FNV offset basis
  for (let i = 0; i < sessionId.length; i++) {
    hash ^= sessionId.charCodeAt(i)
    // FNV prime multiply, kept inside 32-bit unsigned
    hash = Math.imul(hash, 0x01000193) >>> 0
  }

  let out = ''
  for (let i = 0; i < TOKEN_LEN; i++) {
    out += ALPHABET[hash & 0x1f]
    hash >>>= 5
  }
  return out
}
